import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Package, Heart, Calendar, Eye, Loader2, Trash2, Pencil, MapPin, HandCoins } from "lucide-react";
import api, { formatPrice, thumbUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListingCard } from "@/components/ListingCard";
import { OfferCard } from "@/components/OfferCard";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function Profile() {
  const { user, updateProfile, deleteAccount } = useAuth();
  const [params, setParams] = useSearchParams();
  const [mine, setMine] = useState([]);
  const [favs, setFavs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [offersFor, setOffersFor] = useState(null);
  const [myOffers, setMyOffers] = useState([]);
  const tab = ["favorieten", "instellingen", "biedingen"].includes(params.get("tab")) ? params.get("tab") : "advertenties";

  const load = useCallback(() => Promise.all([api.get("/my/listings"), api.get("/my/favorites"), api.get("/my/offers")]).then(([m, f, o]) => { setMine(m.data); setFavs(f.data); setMyOffers(o.data); }).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/listings/${id}`, { status });
    toast.success("Status bijgewerkt");
    load();
  };
  const remove = async (id) => { await api.delete(`/listings/${id}`); toast.success("Advertentie verwijderd"); load(); };
  const payFee = async (id) => {
    try {
      const { data } = await api.post(`/listings/${id}/pay-fee`);
      if (data.payments_enabled && data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.error("Betalen is tijdelijk niet beschikbaar. Probeer het later opnieuw.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Betaling kon niet worden gestart.");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 pb-24 pt-6 md:px-8 md:pb-12">
      <div className="mb-8 flex items-center gap-4">
        <img src={user.avatar} alt="" className="h-20 w-20 rounded-full border border-[#333] bg-[#2A2A2A]" />
        <div>
          <h1 className="font-display text-3xl text-[#F5F5F5] sm:text-4xl">{user.name}</h1>
          <p className="text-[#A0A0A0]">{user.email}</p>
          <p className="mt-1 flex items-center gap-1 text-xs text-[#777]"><Calendar className="h-3 w-3" /> Lid sinds {new Date(user.created_at).toLocaleDateString("nl-NL", { month: "long", year: "numeric" })}{user.account_type === "zakelijk" ? " · Zakelijk" : ""}</p>
        </div>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-3 sm:max-w-md">
        <Stat icon={Package} label="Advertenties" value={mine.length} />
        <Stat icon={Heart} label="Favorieten" value={favs.length} />
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams(v === "advertenties" ? {} : { tab: v })}>
        <TabsList className="mb-6 flex-wrap bg-[#1c1c1c]">
          <TabsTrigger value="advertenties" data-testid="tab-listings" className="data-[state=active]:bg-[#FF6A00] data-[state=active]:text-[#111]">Mijn advertenties</TabsTrigger>
          <TabsTrigger value="favorieten" data-testid="tab-favorites" className="data-[state=active]:bg-[#FF6A00] data-[state=active]:text-[#111]">Favorieten</TabsTrigger>
          <TabsTrigger value="biedingen" data-testid="tab-my-offers" className="data-[state=active]:bg-[#FF6A00] data-[state=active]:text-[#111]">Mijn biedingen</TabsTrigger>
          <TabsTrigger value="instellingen" data-testid="tab-settings" className="data-[state=active]:bg-[#FF6A00] data-[state=active]:text-[#111]">Instellingen</TabsTrigger>
        </TabsList>

        <TabsContent value="advertenties">
          {loading ? <Loader /> : mine.length === 0 ? <Empty text="Je hebt nog geen advertenties geplaatst." cta /> : (
            <div className="space-y-3" data-testid="profile-listings">
              {mine.map((l) => <ManageRow key={l.id} l={l} onStatus={setStatus} onRemove={remove} onEdit={() => setEdit(l)} onOffers={() => setOffersFor(l)} onPayFee={payFee} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="favorieten">
          {loading ? <Loader /> : favs.length === 0 ? <Empty text="Je hebt nog geen favorieten opgeslagen." /> : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="profile-favorites">{favs.map((l) => <ListingCard key={l.id} listing={l} />)}</div>
          )}
        </TabsContent>
        <TabsContent value="biedingen">
          {loading ? <Loader /> : myOffers.length === 0 ? <Empty text="Je hebt nog geen biedingen uitgebracht." /> : (
            <div className="space-y-3" data-testid="profile-my-offers">
              {myOffers.map((o) => <OfferCard key={o.id} offer={o} role="buyer" onChanged={load} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="instellingen">
          <Settings user={user} updateProfile={updateProfile} deleteAccount={deleteAccount} />
        </TabsContent>
      </Tabs>

      {edit && <EditDialog listing={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {offersFor && <OffersDialog listing={offersFor} onClose={() => setOffersFor(null)} />}
    </div>
  );
}

function OffersDialog({ listing, onClose }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => api.get(`/listings/${listing.id}/offers`).then((r) => setOffers(r.data)).finally(() => setLoading(false)), [listing.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]" data-testid="offers-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-2xl"><HandCoins className="h-6 w-6 text-[#FF6A00]" /> BIEDINGEN</DialogTitle></DialogHeader>
        <p className="truncate text-sm text-[#A0A0A0]">{listing.title}</p>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto py-2">
          {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[#FF6A00]" /></div>
            : offers.length === 0 ? <p className="py-8 text-center text-sm text-[#777]" data-testid="offers-empty">Nog geen biedingen ontvangen.</p>
              : offers.map((o) => <OfferCard key={o.id} offer={o} role="seller" onChanged={load} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManageRow({ l, onStatus, onRemove, onEdit, onOffers, onPayFee }) {
  const isDraft = l.status === "draft";
  const isExpired = l.status === "expired";
  const isUnpaid = isDraft || isExpired;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[#333] bg-[#1c1c1c] p-3 sm:flex-row sm:items-center" data-testid={`manage-listing-${l.id}`}>
      <Link to={`/advertentie/${l.id}`} className="flex min-w-0 flex-1 items-center gap-3">
        <img src={thumbUrl(l.images?.[0], 160)} alt="" loading="lazy" decoding="async" className="h-16 w-20 shrink-0 rounded-lg bg-[#2A2A2A] object-cover" />
        <div className="min-w-0">
          <p className="truncate font-semibold text-[#F5F5F5]">{l.title}</p>
          <p className="font-display text-lg text-[#FF6A00]">{formatPrice(l.price)}</p>
          <p className="flex items-center gap-3 text-xs text-[#777]"><span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {l.views || 0}</span><span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {l.favorites_count || 0}</span>
            {l.status === "reserved" && <span className="rounded-full bg-[#FFB27A] px-2 py-0.5 font-bold text-[#111]">Gereserveerd</span>}
            {l.status === "sold" && <span className="rounded-full bg-[#EF4444] px-2 py-0.5 font-bold text-white">Verkocht</span>}
            {isDraft && <span data-testid={`draft-badge-${l.id}`} className="rounded-full bg-[#3a3a3a] px-2 py-0.5 font-bold text-[#FFB27A]">Concept · niet online</span>}
            {isExpired && <span data-testid={`expired-badge-${l.id}`} className="rounded-full bg-[#EF4444]/20 px-2 py-0.5 font-bold text-[#EF4444]">Verlopen</span>}
          </p>
          {isDraft && <p className="mt-0.5 text-xs text-[#FFB27A]" data-testid={`draft-note-${l.id}`}>Betaal €{l.listing_fee || 10} om je advertentie online te zetten (4 weken).</p>}
          {isExpired && <p className="mt-0.5 text-xs text-[#EF4444]" data-testid={`expired-note-${l.id}`}>Verleng binnen 7 dagen met €{l.listing_fee || 10}, anders wordt de advertentie verwijderd.</p>}
          {l.status === "reserved" && l.reserved_at && (
            <p className="mt-0.5 text-xs text-[#FFB27A]" data-testid={`reserved-since-${l.id}`}>Gereserveerd sinds {new Date(l.reserved_at).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })} · verloopt na 7 dagen zonder activiteit</p>
          )}
        </div>
      </Link>
      <div className="flex flex-wrap gap-2">
        {isUnpaid ? (
          <button data-testid={`pay-fee-${l.id}`} onClick={() => onPayFee(l.id)} className="flex items-center gap-1 rounded-full bg-[#FF6A00] px-4 py-2 text-xs font-bold text-[#111] hover:bg-[#FFB27A]">
            {isExpired ? `Verleng €${l.listing_fee || 10}` : `Betaal €${l.listing_fee || 10} & plaats`}
          </button>
        ) : (
          <>
            {l.status !== "reserved" && l.status !== "sold" && <SmallBtn testid={`reserve-${l.id}`} onClick={() => onStatus(l.id, "reserved")}>Reserveren</SmallBtn>}
            {l.status !== "sold" && <SmallBtn testid={`sold-${l.id}`} onClick={() => onStatus(l.id, "sold")}>Markeer als verkocht</SmallBtn>}
            {l.status === "reserved" && <SmallBtn testid={`cancel-reservation-${l.id}`} onClick={() => onStatus(l.id, "active")}>Reservering annuleren</SmallBtn>}
            {l.status === "sold" && <SmallBtn testid={`reactivate-${l.id}`} onClick={() => onStatus(l.id, "active")}>Heractiveren</SmallBtn>}
            {l.allow_offers !== false && <SmallBtn testid={`offers-${l.id}`} onClick={onOffers}><HandCoins className="h-3.5 w-3.5" /> Biedingen</SmallBtn>}
          </>
        )}
        <SmallBtn testid={`edit-${l.id}`} onClick={onEdit}><Pencil className="h-3.5 w-3.5" /></SmallBtn>
        <AlertDialog>
          <AlertDialogTrigger asChild><button data-testid={`delete-${l.id}`} className="rounded-full border border-[#EF4444]/40 p-2 text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="h-3.5 w-3.5" /></button></AlertDialogTrigger>
          <AlertDialogContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]">
            <AlertDialogHeader><AlertDialogTitle>Advertentie verwijderen?</AlertDialogTitle><AlertDialogDescription className="text-[#A0A0A0]">Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="border-[#333] bg-transparent text-[#F5F5F5]">Annuleren</AlertDialogCancel><AlertDialogAction data-testid={`confirm-delete-${l.id}`} onClick={() => onRemove(l.id)} className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Verwijderen</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function SmallBtn({ children, onClick, testid }) {
  return <button data-testid={testid} onClick={onClick} className="flex items-center gap-1 rounded-full border border-[#333] px-3 py-2 text-xs font-semibold text-[#F5F5F5] hover:border-[#FF6A00] hover:text-[#FF6A00]">{children}</button>;
}

function EditDialog({ listing, onClose, onSaved }) {
  const [f, setF] = useState({ title: listing.title, price: String(listing.price), description: listing.description });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/listings/${listing.id}`, { title: f.title, price: parseFloat(f.price), description: f.description }); toast.success("Opgeslagen"); onSaved(); }
    catch { toast.error("Opslaan mislukt"); } finally { setSaving(false); }
  };
  const inp = "w-full rounded-lg border border-[#333] bg-[#2A2A2A] p-3 text-[#F5F5F5] outline-none focus:border-[#FF6A00]";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]" data-testid="edit-listing-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Advertentie bewerken</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="edit-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} />
          <input data-testid="edit-price" type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={inp} />
          <textarea data-testid="edit-description" rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={`${inp} resize-none`} />
        </div>
        <DialogFooter><button onClick={save} disabled={saving} data-testid="edit-save-btn" className="flex items-center justify-center gap-2 rounded-full bg-[#FF6A00] px-6 py-2.5 font-bold text-[#111]">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Opslaan</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Settings({ user, updateProfile, deleteAccount }) {
  const [f, setF] = useState({ name: user.name, city: user.city || "", postcode: user.postcode || "", radius_km: user.radius_km || 25, account_type: user.account_type || "particulier" });
  const [saving, setSaving] = useState(false);
  const inp = "w-full rounded-lg border border-[#333] bg-[#2A2A2A] p-3 text-[#F5F5F5] outline-none focus:border-[#FF6A00]";
  const save = async () => { setSaving(true); try { await updateProfile({ ...f, radius_km: parseInt(f.radius_km) }); toast.success("Profiel opgeslagen"); } catch { toast.error("Mislukt"); } finally { setSaving(false); } };
  return (
    <div className="max-w-lg space-y-4" data-testid="settings-panel">
      <div><label className="mb-1.5 block text-sm font-semibold">Naam</label><input data-testid="settings-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="mb-1.5 block text-sm font-semibold">Plaats</label><input data-testid="settings-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="Bijv. Utrecht" className={inp} /></div>
        <div><label className="mb-1.5 block text-sm font-semibold">Postcode</label><input data-testid="settings-postcode" value={f.postcode} onChange={(e) => setF({ ...f, postcode: e.target.value })} placeholder="1234 AB" className={inp} /></div>
      </div>
      <div><label className="mb-1.5 block text-sm font-semibold">Zoekstraal: {f.radius_km} km</label><input data-testid="settings-radius" type="range" min="1" max="100" value={f.radius_km} onChange={(e) => setF({ ...f, radius_km: e.target.value })} className="w-full accent-[#FF6A00]" /></div>
      <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="flex items-center gap-2 rounded-full bg-[#FF6A00] px-6 py-3 font-bold text-[#111]">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Opslaan</button>

      <div className="mt-8 rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/5 p-4">
        <p className="font-display text-lg text-[#EF4444]">GEVARENZONE</p>
        <p className="mb-3 text-sm text-[#A0A0A0]">Je account en al je advertenties worden permanent verwijderd.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild><button data-testid="delete-account-btn" className="rounded-full border border-[#EF4444] px-5 py-2.5 font-semibold text-[#EF4444] hover:bg-[#EF4444]/10">Account verwijderen</button></AlertDialogTrigger>
          <AlertDialogContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]">
            <AlertDialogHeader><AlertDialogTitle>Account verwijderen?</AlertDialogTitle><AlertDialogDescription className="text-[#A0A0A0]">Dit verwijdert je account, advertenties en chats permanent.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="border-[#333] bg-transparent text-[#F5F5F5]">Annuleren</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-account" onClick={deleteAccount} className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Definitief verwijderen</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) { return <div className="flex items-center gap-3 rounded-xl border border-[#333] bg-[#1c1c1c] p-4"><Icon className="h-6 w-6 text-[#FF6A00]" /><div><p className="font-display text-2xl text-[#F5F5F5]">{value}</p><p className="text-xs text-[#A0A0A0]">{label}</p></div></div>; }
function Empty({ text, cta }) { return <div className="flex flex-col items-center justify-center rounded-2xl border border-[#333] bg-[#1c1c1c] py-16 text-center"><p className="text-[#A0A0A0]">{text}</p>{cta && <Link to="/plaatsen" className="mt-4 rounded-full bg-[#FF6A00] px-5 py-2.5 font-bold text-[#111]">Plaats advertentie</Link>}</div>; }
function Loader() { return <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">{["p1","p2","p3","p4"].map((k) => <div key={k} className="aspect-[4/3] animate-pulse rounded-xl border border-[#333] bg-[#1c1c1c]" />)}</div>; }
